import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { EmailConfirmationNotice, useEmailConfirmation } from '@/components/EmailConfirmationBanner';
import { BetaBanner } from '@/components/rides/BetaBanner';
import { BottomTabBar, useBottomTabBarVisible } from '@/components/rides/BottomTabBar';
import { AppHeader } from '@/components/shell/AppHeader';
import { SiteFooter } from '@/components/shell/SiteFooter';
import { useIsFocusMode } from '@/components/shell/focusMode';
import { isNoFooterRoute, isPublicRoute } from '@/components/shell/routes';
import { useCopy } from '@/lib/settings';

/**
 * App frame (see DESIGN.md → Navigation model):
 * - header band: Acts Church wordmark + "Carpool"; theme button (signed out) or avatar menu
 *   (signed in); text navigation on desktop;
 * - one banner at a time (confirm your email wins over beta);
 * - the routed page in a centred 600 px column;
 * - footer: slim on public pages, desktop-only band on signed-in pages, none in step flows,
 *   on ride day or while a sticky action bar is on screen;
 * - bottom tab bar on phones for signed-in screens (content is padded so nothing hides behind it).
 */
export function RidesShell() {
  const { pathname } = useLocation();
  const { isAuthenticated } = useEnrichedAuth();
  const { t } = useCopy();
  const appName = t('app.name');
  const tabBar = useBottomTabBarVisible();
  const focus = useIsFocusMode();
  const email = useEmailConfirmation();

  useEffect(() => { document.title = appName; }, [appName]);
  // New page: start at the top (the header is sticky, so a kept scroll position hides the title).
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  const publicPage = isPublicRoute(pathname) || !isAuthenticated;
  const footer = focus || isNoFooterRoute(pathname) ? null : publicPage ? 'public' : 'app';

  return (
    <div className={`rides-app${tabBar ? ' has-tabbar' : ''}`}>
      <a href="#main" className="rides-skip">Skip to content</a>
      <AppHeader />
      {email.visible ? <EmailConfirmationNotice state={email} /> : <BetaBanner />}
      <main id="main" className="rides-content" tabIndex={-1}>
        <Outlet />
      </main>
      {footer && <SiteFooter variant={footer} />}
      <BottomTabBar />
    </div>
  );
}
